import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { Compass, Eye, EyeOff } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1502003148287-a82ef80a6abc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxhdXRoZW50aWMlMjB0cmF2ZWwlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg2MzI1MTU0fDA&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      let pendingCopy = null;
      try { pendingCopy = sessionStorage.getItem("copy_share_id"); } catch (e) {}
      if (pendingCopy) {
        try {
          const { api } = await import("../lib/api");
          const { data } = await api.post("/trips/copy", { share_id: pendingCopy });
          sessionStorage.removeItem("copy_share_id");
          navigate(`/trips/${data.id}?personalize=1`);
          return;
        } catch (e) { /* fall through to default */ }
      }
      const intent = sessionStorage.getItem("wm_checkout_intent");
      if (intent) {
        sessionStorage.removeItem("wm_checkout_intent");
        try {
          const { api } = await import("../lib/api");
          const { plan, destination, sourceTripId } = JSON.parse(intent);
          const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin, destination: destination || null, source_trip_id: sourceTripId || null });
          window.location.href = data.checkout_url;
          return;
        } catch (e) { navigate("/plans"); return; }
      }
      navigate("/plan");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
      <Seo title="Sign in" description="Sign in to WanderlyMe to plan, save and revisit your AI-generated trips." path="/auth" noindex />
      <div className="hidden lg:block relative">
        <img src={HERO} alt="travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3B2B]/70 to-transparent" />
        <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 text-[#FDFBF7]">
          <Compass className="w-6 h-6" />
          <span className="font-display text-2xl">WanderlyMe</span>
        </Link>
        <p className="absolute bottom-10 left-8 right-8 font-display text-4xl text-[#FDFBF7] leading-tight">
          Your next trip is one budget away.
        </p>
      </div>

      <div className="flex items-center justify-center p-8 grain relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <Compass className="w-6 h-6 text-[#C8553D]" />
            <span className="font-display text-2xl">WanderlyMe</span>
          </Link>
          <h1 className="font-display text-4xl tracking-tight mb-1">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {mode === "login" ? "Sign in to see your saved trips." : "Start planning in seconds."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input
                data-testid="auth-name"
                className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
            <input
              data-testid="auth-email"
              type="email"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <div className="relative">
              <input
                data-testid="auth-password"
                type={showPass ? "text" : "password"}
                className="w-full px-4 py-3 pr-12 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                data-testid="auth-toggle-password"
                onClick={() => setShowPass((s) => !s)}
                aria-label={showPass ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1A3B2B] transition-colors"
              >
                {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {error && (
              <p data-testid="auth-error" className="text-sm text-[#C8553D]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="auth-submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          {mode === "login" && (
            <div className="mt-4">
              <Link
                to="/forgot-password"
                data-testid="auth-forgot-link"
                className="text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          <button
            data-testid="auth-toggle"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            className="mt-6 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
          >
            {mode === "login"
              ? "New here? Create an account"
              : "Already have an account? Sign in"}
          </button>
        </motion.div>
      </div>
    </div>
      <Footer />
    </div>
  );
}
